import { useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import EcoImg from 'eco-design/base/eco-img';
import EcoSpace from 'eco-design/base/eco-space';
import { useWebpOrJpg } from '../../hooks/useWebpOrJpg';
import css from './index.module.scss';
import Dialog from './Dialog';
import { useLang } from '@/app/utils/useLang';
import gtm from '@/utils/gtm';
import getLocal from '@/app/utils/i18n';

const App: React.FC = () => {
    const { locale } = getLocal();
    const { t } = useLang('dpux.App');
    const { t: t2 } = useLang('dpux');
    const webpOrJpg = useWebpOrJpg('#delta-pro-ultra-x-App');

    const list = [
        {
            img: 'https://websiteoss.ecoflow.com/upload/image/1757469683938/desktop.png',
            title: t('item3_title'),
            subTitle: t('item3_subTitle'),
            desc: t('item3_desc'),
        },
        {
            img: 'https://websiteoss.ecoflow.com/upload/image/1757469684248/laptop.png',
            title: t('item2_title'),
            subTitle: t('item2_subTitle'),
            desc: t('item2_desc'),
        },
        {
            img: 'https://websiteoss.ecoflow.com/upload/image/1757469684097/phone.png',
            title: t('item1_title'),
            subTitle: t('item1_subTitle'),
            desc: t('item1_desc'),
        },
    ];

    const [open, setOpen] = useState(false);

    /** 动画 */
    const sectionRef = useRef<HTMLDivElement>(null);
    const [enter, setEnter] = useState(false);
    useEffect(() => {
        if (!sectionRef.current) return;
        const title = sectionRef.current?.querySelector('.info .title') as HTMLElement;
        const desc = sectionRef.current?.querySelector('.info .desc') as HTMLElement;
        const tips = sectionRef.current?.querySelector('.footer-tips') as HTMLElement;
        const screen = AniContainer.create({
            triggerElement: sectionRef.current,
            triggerHook: 1,
            offset: '20%',
            duration: '100%',
            onEnter: () => {
                title.classList.add('enter');
                desc.classList.add('enter');
                tips?.classList.add('enter');
            },
            onProcess: (process: number) => {
                if (process <= 0) {
                    title.classList.remove('enter');
                    desc.classList.remove('enter');
                    tips?.classList.remove('enter');
                }
            },
        });

        const cards = sectionRef.current?.querySelector('.app-cards .swiper-wrapper') as HTMLElement;
        const btn = sectionRef.current?.querySelector('.app-btn') as HTMLElement;
        const cardsAni = AniContainer.create({
            triggerElement: cards,
            triggerHook: 1,
            offset: '20%',
            duration: '100%',
            onEnter: () => {
                setEnter(true);
                setTimeout(() => {
                    btn.classList.add('enter');
                }, 380);
            },
            onProcess: (process: number) => {
                if (process <= 0) {
                    setEnter(false);
                    btn.classList.remove('enter');
                }
            },
        });
        return () => {
            screen.destroy?.();
            cardsAni.destroy?.();
        };
    }, []);

    return (
        <>
            <section
                className={`${css.section} delta-pro-ultra-x-App dpux-App GtmModuleData-section`}
                id="delta-pro-ultra-x-App"
                data-section-id={'delta-pro-ultra-x-App'}
                data-section-title={t('title')}
                ref={sectionRef}
            >
                <div className="info">
                    <div className="title ani-1">{t('title')}</div>
                    <div className="desc ani-2">{t('desc')}</div>
                </div>
                <EcoImg
                    className="app-bg"
                    pc={webpOrJpg('https://websiteoss.ecoflow.com/upload/image/1759049587629/App.jpg')}
                    h5={webpOrJpg(
                        'https://websiteoss.ecoflow.com/upload/image/1758941953262/AppM.jpg',
                        'https://websiteoss.ecoflow.com/upload/image/1758941948141/AppM.webp',
                    )}
                />
                <div className="app-footer">
                    <div className="app-footer-content">
                        <Swiper className="app-cards" slidesPerView={'auto'}>
                            {list.map((i, idx) => (
                                <SwiperSlide className={`app-card-item ani-1 ${enter ? 'enter' : ''}`} key={idx}>
                                    <EcoImg pc={i.img} isOpenLazy />
                                    <div className="app-card-item-title">{i.title}</div>
                                    <div className="app-card-item-subTitle">{i.subTitle}</div>
                                    <div className="app-card-item-desc">{i.desc}</div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                </div>
                <div
                    className={`app-btn GtmModuleData-btn-text`}
                    onClick={() => {
                        setOpen(true);
                        gtm.push({
                            event: 'uaEvent',
                            event_name: 'product_details',
                            tab: t('title'),
                            button_name: t('btn'),
                            __btn_id1: `dpux-App .app-btn`,
                            __btn_id2: t('btn'),
                            __btn_type: 'text',
                        });
                    }}
                >
                    {t('btn')}
                    <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
                        <g clipPath="url(#clip0_2801_68529)">
                            <path
                                d="M10.633 18.885C15.3668 18.885 19.2044 15.0475 19.2044 10.3136C19.2044 5.57975 15.3668 1.74219 10.633 1.74219C5.89908 1.74219 2.06152 5.57975 2.06152 10.3136C2.06152 15.0475 5.89908 18.885 10.633 18.885Z"
                                fill="#3F68E0"
                            />
                            <path d="M7.50781 10.3125H13.7578" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M10.6328 7.18945V13.4395" stroke="white" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                        </g>
                        <defs>
                            <clipPath id="clip0_2801_68529">
                                <rect width="20" height="20" fill="white" transform="translate(0.632812 0.314453)" />
                            </clipPath>
                        </defs>
                    </svg>
                </div>

                {locale === 'ca' && <div className="footer-tips ani-3">{t2('item_Remarks3')}</div>}
            </section>
            <Dialog open={open} onClose={() => setOpen(false)} />
            <EcoSpace />
        </>
    );
};

export default App;
